import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { application } from '../../../../../services/application';

export const ALERT_EVENT = 'alert-message';
export const SYNC_DEBOUNCE_MS = 500;

export const isForeignOrganizationPush = (payload, activeOrganizationId) =>
  Boolean(
    activeOrganizationId && payload?.feed?.organizationId && payload.feed.organizationId !== activeOrganizationId
  );

export const shouldSyncForPush = (payload, { isArchive, activeOrganizationId }) =>
  Boolean(payload?.feed) && !isArchive && !isForeignOrganizationPush(payload, activeOrganizationId);

const flattenItems = (groups = []) => groups.flatMap((group) => group?.items || []);

function findFirstVisibleCard(container) {
  const containerTop = container.getBoundingClientRect().top;
  for (const card of container.querySelectorAll('[data-card-id]')) {
    const rect = card.getBoundingClientRect();
    if (rect.bottom > containerTop) {
      return { id: card.dataset.cardId, offset: rect.top - containerTop, scrollTop: container.scrollTop };
    }
  }
  return null;
}

function captureAnchor(container) {
  return (
    (container && findFirstVisibleCard(container)) || { id: null, offset: 0, scrollTop: container?.scrollTop || 0 }
  );
}

function callSync(onSyncList) {
  try {
    return Promise.resolve(onSyncList()).catch(() => undefined);
  } catch {
    return Promise.resolve();
  }
}

function restoreAnchor(container, anchor) {
  if (!anchor.id) return;
  const card = container.querySelector(`[data-card-id="${anchor.id}"]`);
  if (!card) return;
  const offset = card.getBoundingClientRect().top - container.getBoundingClientRect().top;
  container.scrollTop += offset - anchor.offset;
}

export function useLiveSync({ isArchive, activeOrganizationId, pending, items, scrollRef, onSyncList, isItemEnabled }) {
  const [newCount, setNewCount] = useState(0);
  const anchorRef = useRef(null);
  const timerRef = useRef(null);
  const queuedRef = useRef(false);
  const syncingRef = useRef(false);
  const mountedRef = useRef(true);
  const pendingRef = useRef(pending);
  const previousIdsRef = useRef(new Set(flattenItems(items).map((item) => item.id)));
  const settingsRef = useRef({});

  pendingRef.current = pending;
  settingsRef.current = { isArchive, activeOrganizationId, onSyncList, isItemEnabled };

  const scheduleRef = useRef(null);

  const flushQueued = useCallback(() => {
    if (!queuedRef.current || pendingRef.current || syncingRef.current || !mountedRef.current) return;
    queuedRef.current = false;
    scheduleRef.current();
  }, []);

  const runSync = useCallback(() => {
    timerRef.current = null;
    if (pendingRef.current || syncingRef.current) {
      queuedRef.current = true;
      return;
    }

    const anchor = captureAnchor(scrollRef.current);
    anchorRef.current = anchor;
    syncingRef.current = true;
    callSync(settingsRef.current.onSyncList).finally(() => {
      syncingRef.current = false;
      if (anchorRef.current === anchor) anchorRef.current = null;
      flushQueued();
    });
  }, [scrollRef, flushQueued]);

  const scheduleSync = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(runSync, SYNC_DEBOUNCE_MS);
  }, [runSync]);

  scheduleRef.current = scheduleSync;

  useEffect(() => {
    const handler = (payload) => {
      const { isArchive: archiveView, activeOrganizationId: organizationId } = settingsRef.current;
      if (shouldSyncForPush(payload, { isArchive: archiveView, activeOrganizationId: organizationId })) {
        scheduleSync();
      }
    };

    mountedRef.current = true;
    application.on(ALERT_EVENT, handler);
    return () => {
      mountedRef.current = false;
      application.off(ALERT_EVENT, handler);
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [scheduleSync]);

  useEffect(() => {
    if (pending) anchorRef.current = null;
    else flushQueued();
  }, [pending, flushQueued]);

  useEffect(() => {
    anchorRef.current = null;
    setNewCount(0);
  }, [isArchive, activeOrganizationId]);

  useLayoutEffect(() => {
    const previousIds = previousIdsRef.current;
    const flat = flattenItems(items);
    previousIdsRef.current = new Set(flat.map((item) => item.id));

    const anchor = anchorRef.current;
    if (!anchor) return;
    anchorRef.current = null;

    const container = scrollRef.current;
    if (!container || anchor.scrollTop <= 0) return;

    restoreAnchor(container, anchor);

    const added = flat.filter((item) => !previousIds.has(item.id) && settingsRef.current.isItemEnabled(item));
    if (added.length) setNewCount((count) => count + added.length);
  }, [items, scrollRef]);

  const clearNewCount = useCallback(() => setNewCount(0), []);

  const scrollToTop = useCallback(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = 0;
    setNewCount(0);
  }, [scrollRef]);

  const onScroll = useCallback((event) => {
    anchorRef.current = null;
    if (event.currentTarget.scrollTop === 0) setNewCount(0);
  }, []);

  return { newCount, clearNewCount, scrollToTop, onScroll };
}
